import { CommonModule } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { finalize, map } from 'rxjs';
import { DevicesProfileModalTemplateComponent } from '../devices-profile-modal-template/devices-profile-modal-template.component';
import { ErrorBlockComponent } from '../error-block/error-block.component';

type DeviceStatus = 'active' | 'maintenance' | 'broken' | 'retired';

interface DeviceProfileApiItem {
  id: string;
  companyId: string;
  companyDisplay: string | null;
  clinicId: string | null;
  clinicName: string | null;
  deviceTypeId: string;
  deviceTypeName: string | null;
  serialNumber: string | null;
  brand: string | null;
  model: string | null;
  manufactureDateText: string | null;
  lastServiceDateText: string | null;
  addedDateText: string | null;
  notes: string | null;
  photos: Array<{ key?: string; url?: string } | string> | null;
  status: DeviceStatus | null;
  createdAt: string | null;
  updatedAt: string | null;
  lifecycleEvents: DeviceLifecycleEventApiItem[] | null;
}

interface DeviceLifecycleEventApiItem {
  id: string;
  eventType: string;
  title: string | null;
  notes: string | null;
  performedAt: string;
  createdAt: string;
}

interface ProfileViewModel {
  id: string;
  companyId: string;
  companyDisplay: string;
  clinicId: string;
  clinicName: string;
  deviceTypeId: string;
  deviceTypeName: string;
  serialNumber: string;
  brand: string;
  model: string;
  status: string | null;
  createdAtText: string;
  updatedAtText: string;
  manufactureDateText: string;
  lastServiceDateText: string;
  addedDateText: string;
  notes: string;
  photosText: string;
  photosPreview: Array<{ key?: string; url: string; name?: string }>;
  lifecycleEventsText: string;
  latestLifecycleEventText: string;
  showStatus: boolean;
  showClinic: boolean;
  showSerialNumber: boolean;
  showCreatedAt: boolean;
  showUpdatedAt: boolean;
  showManufactureDate: boolean;
  showLastServiceDate: boolean;
  showAddedDate: boolean;
  showNotes: boolean;
  showPhotos: boolean;
  showLifecycle: boolean;
}

@Component({
  selector: 'lib-devices-profile-modal-container',
  standalone: true,
  imports: [CommonModule, DevicesProfileModalTemplateComponent, ErrorBlockComponent],
  template: `
    @if (isOpen) {
      @if (isLoading) {
        <div class="devices-profile-modal-container__backdrop" (click)="close.emit()">
          <div class="devices-profile-modal-container__loading" (click)="$event.stopPropagation()">
            Loading device profile...
          </div>
        </div>
      } @else if (errorText) {
        <div class="devices-profile-modal-container__backdrop" (click)="close.emit()">
          <div class="devices-profile-modal-container__error" (click)="$event.stopPropagation()">
            <lib-error-block variant="error" [message]="errorText" />
            <div class="devices-profile-modal-container__actions">
              <button type="button" (click)="close.emit()">{{ closeText }}</button>
            </div>
          </div>
        </div>
      } @else if (viewModel) {
        <lib-devices-profile-modal-template
          [title]="title"
          [closeText]="closeText"
          [showCompanyId]="resolvedShowCompanyId"
          [id]="viewModel.id"
          [companyDisplay]="viewModel.companyDisplay"
          [companyId]="viewModel.companyId"
          [clinicName]="viewModel.clinicName"
          [clinicId]="viewModel.clinicId"
          [showClinic]="viewModel.showClinic"
          [deviceTypeName]="viewModel.deviceTypeName"
          [deviceTypeId]="viewModel.deviceTypeId"
          [serialNumber]="viewModel.serialNumber"
          [showSerialNumber]="viewModel.showSerialNumber"
          [brand]="viewModel.brand"
          [model]="viewModel.model"
          [status]="viewModel.status"
          [showStatus]="viewModel.showStatus"
          [createdAtText]="viewModel.createdAtText"
          [showCreatedAt]="viewModel.showCreatedAt"
          [updatedAtText]="viewModel.updatedAtText"
          [showUpdatedAt]="viewModel.showUpdatedAt"
          [manufactureDateText]="viewModel.manufactureDateText"
          [showManufactureDate]="viewModel.showManufactureDate"
          [lastServiceDateText]="viewModel.lastServiceDateText"
          [showLastServiceDate]="viewModel.showLastServiceDate"
          [addedDateText]="viewModel.addedDateText"
          [showAddedDate]="viewModel.showAddedDate"
          [notes]="viewModel.notes"
          [showNotes]="viewModel.showNotes"
          [photosText]="viewModel.photosText"
          [photosPreview]="viewModel.photosPreview"
          [showPhotos]="viewModel.showPhotos"
          [lifecycleEventsText]="viewModel.lifecycleEventsText"
          [latestLifecycleEventText]="viewModel.latestLifecycleEventText"
          [showLifecycle]="viewModel.showLifecycle"
          [actionOneLabel]="canArchive && viewModel.status !== 'retired' ? archiveLabel : ''"
          [actionOneDisabled]="actionsDisabled"
          [actionTwoLabel]="canDelete ? deleteLabel : ''"
          [actionTwoDisabled]="actionsDisabled"
          [primaryActionLabel]="canEdit ? editLabel : ''"
          [primaryActionDisabled]="actionsDisabled"
          keyboardSubmitMode="primary"
          (close)="close.emit()"
          (actionOne)="archive.emit()"
          (actionTwo)="delete.emit()"
          (primaryAction)="edit.emit()"
        />
      }
    }
  `,
  styles: [`
    .devices-profile-modal-container__backdrop {
      position: fixed;
      inset: 0;
      background: rgba(15, 23, 42, 0.45);
      display: grid;
      place-items: center;
      padding: 12px;
      z-index: 1000;
    }

    .devices-profile-modal-container__loading,
    .devices-profile-modal-container__error {
      width: min(640px, calc(100vw - 24px));
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      background: #ffffff;
      padding: 14px;
      color: #334155;
    }

    .devices-profile-modal-container__actions {
      margin-top: 10px;
      display: flex;
      justify-content: flex-end;
    }

    .devices-profile-modal-container__actions button {
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      background: #ffffff;
      color: #0f172a;
      padding: 8px 12px;
      font-weight: 600;
      cursor: pointer;
    }
  `],
})
export class DevicesProfileModalContainerComponent implements OnChanges {
  private readonly http = inject(HttpClient);

  @Input() public isOpen = false;
  @Input() public apiBaseUrl = '';
  @Input() public deviceId: string | null = null;
  @Input() public companyId: string | null = null;
  @Input() public role: string | null = null;
  @Input() public title = 'Device profile';
  @Input() public closeText = 'Close';
  @Input() public showCompanyId = false;
  @Input() public canArchive = false;
  @Input() public canDelete = false;
  @Input() public canEdit = false;
  @Input() public actionsDisabled = false;
  @Input() public archiveLabel = 'Archive';
  @Input() public deleteLabel = 'Delete';
  @Input() public editLabel = 'Edit';

  @Output() public readonly close = new EventEmitter<void>();
  @Output() public readonly archive = new EventEmitter<void>();
  @Output() public readonly delete = new EventEmitter<void>();
  @Output() public readonly edit = new EventEmitter<void>();

  protected isLoading = false;
  protected errorText: string | null = null;
  protected viewModel: ProfileViewModel | null = null;

  protected get resolvedShowCompanyId(): boolean {
    if (this.showCompanyId) {
      return true;
    }

    return this.role === 'system_admin';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isOpen) {
      this.isLoading = false;
      this.errorText = null;
      this.viewModel = null;
      return;
    }

    const shouldReload =
      !!changes['isOpen'] ||
      !!changes['deviceId'] ||
      !!changes['apiBaseUrl'] ||
      !!changes['companyId'];

    if (shouldReload) {
      this.load();
    }
  }

  private load(): void {
    const baseUrl = this.normalizeBaseUrl(this.apiBaseUrl);
    const deviceId = this.deviceId?.trim() ?? '';
    const scopedCompanyId = this.role === 'system_admin' ? (this.companyId?.trim() ?? '') : '';

    if (!baseUrl || !deviceId) {
      this.viewModel = null;
      this.errorText = 'Device context is missing.';
      return;
    }

    if (this.role === 'system_admin' && !scopedCompanyId) {
      this.viewModel = null;
      this.errorText = 'Select company in global header.';
      return;
    }

    this.isLoading = true;
    this.errorText = null;

    const scopedParams = this.buildScopedParams(scopedCompanyId);

    this.http.get<unknown>(`${baseUrl}/devices/${deviceId}/detail`, { params: scopedParams }).pipe(
      map((item) => this.normalizeDevice(item)),
      map((device) => this.buildViewModel(device)),
      finalize(() => {
        this.isLoading = false;
      }),
    ).subscribe({
      next: (viewModel) => {
        this.viewModel = viewModel;
      },
      error: () => {
        this.viewModel = null;
        this.errorText = 'Failed to load device profile.';
      },
    });
  }

  private buildViewModel(device: DeviceProfileApiItem): ProfileViewModel {
    const companyDisplay =
      this.cleanString(device.companyDisplay) ??
      this.readableId(device.companyId);

    const clinicName =
      this.cleanString(device.clinicName) ??
      this.readableId(device.clinicId ?? '');

    const deviceTypeName =
      this.cleanString(device.deviceTypeName) ??
      this.readableId(device.deviceTypeId);

    const photos = device.photos ?? [];
    const lifecycleEvents = device.lifecycleEvents ?? [];
    const photosPreview = this.extractPhotoPreviews(photos);
    const latestLifecycleEvent = lifecycleEvents[0] ?? null;

    return {
      id: device.id,
      companyId: device.companyId,
      companyDisplay,
      clinicId: device.clinicId ?? '',
      clinicName,
      deviceTypeId: device.deviceTypeId,
      deviceTypeName,
      serialNumber: this.cleanString(device.serialNumber) ?? '—',
      brand: this.cleanString(device.brand) ?? '—',
      model: this.cleanString(device.model) ?? '—',
      status: device.status,
      createdAtText: this.formatDate(device.createdAt),
      updatedAtText: this.formatDate(device.updatedAt),
      manufactureDateText: this.cleanString(device.manufactureDateText) ?? '—',
      lastServiceDateText: this.cleanString(device.lastServiceDateText) ?? '—',
      addedDateText: this.cleanString(device.addedDateText) ?? '—',
      notes: this.cleanString(device.notes) ?? '—',
      photosText: String(photos.length),
      photosPreview,
      lifecycleEventsText: String(lifecycleEvents.length),
      latestLifecycleEventText: latestLifecycleEvent
        ? this.formatLifecycleEvent(latestLifecycleEvent)
        : '—',
      showStatus: device.status !== null,
      showClinic: device.clinicId !== null || device.clinicName !== null,
      showSerialNumber: device.serialNumber !== null,
      showCreatedAt: device.createdAt !== null,
      showUpdatedAt: device.updatedAt !== null,
      showManufactureDate: device.manufactureDateText !== null,
      showLastServiceDate: device.lastServiceDateText !== null,
      showAddedDate: device.addedDateText !== null,
      showNotes: device.notes !== null,
      showPhotos: device.photos !== null,
      showLifecycle: device.lifecycleEvents !== null,
    };
  }

  private normalizeDevice(value: unknown): DeviceProfileApiItem {
    const source = (value ?? {}) as Record<string, unknown>;
    return {
      id: this.readString(source, 'id') ?? '',
      companyId: this.readString(source, 'companyId', 'company_id') ?? '',
      companyDisplay: this.readNullableString(source, 'companyDisplay', 'company_display'),
      clinicId: this.hasAnyKey(source, 'clinicId', 'clinic_id')
        ? this.readNullableString(source, 'clinicId', 'clinic_id')
        : null,
      clinicName: this.hasAnyKey(source, 'clinicName', 'clinic_name', 'clinic')
        ? this.readNullableString(source, 'clinicName', 'clinic_name', 'clinic')
        : null,
      deviceTypeId: this.readString(source, 'deviceTypeId', 'device_type_id') ?? '',
      deviceTypeName: this.readNullableString(source, 'deviceTypeName', 'device_type_name'),
      serialNumber: this.hasAnyKey(source, 'serialNumber', 'serial_number')
        ? this.readNullableString(source, 'serialNumber', 'serial_number')
        : null,
      brand: this.readNullableString(source, 'brand'),
      model: this.readNullableString(source, 'model'),
      manufactureDateText: this.hasAnyKey(source, 'manufactureDateText', 'manufacture_date_text')
        ? this.readNullableString(source, 'manufactureDateText', 'manufacture_date_text')
        : null,
      lastServiceDateText: this.hasAnyKey(source, 'lastServiceDateText', 'last_service_date_text')
        ? this.readNullableString(source, 'lastServiceDateText', 'last_service_date_text')
        : null,
      addedDateText: this.hasAnyKey(source, 'addedDateText', 'added_date_text')
        ? this.readNullableString(source, 'addedDateText', 'added_date_text')
        : null,
      notes: Object.prototype.hasOwnProperty.call(source, 'notes')
        ? this.readNullableString(source, 'notes')
        : null,
      photos: Array.isArray(source['photos'])
        ? (source['photos'] as Array<{ key?: string; url?: string } | string>)
        : null,
      status: Object.prototype.hasOwnProperty.call(source, 'status')
        ? this.normalizeStatus(this.readString(source, 'status'))
        : null,
      createdAt: this.hasAnyKey(source, 'createdAt', 'created_at')
        ? (this.readString(source, 'createdAt', 'created_at') ?? null)
        : null,
      updatedAt: this.hasAnyKey(source, 'updatedAt', 'updated_at')
        ? (this.readString(source, 'updatedAt', 'updated_at') ?? null)
        : null,
      lifecycleEvents: Object.prototype.hasOwnProperty.call(source, 'lifecycleEvents')
        ? this.normalizeLifecycleEvents(source['lifecycleEvents'])
        : null,
    };
  }

  private normalizeLifecycleEvents(value: unknown): DeviceLifecycleEventApiItem[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.flatMap((item) => {
      if (!item || typeof item !== 'object') {
        return [];
      }

      const source = item as Record<string, unknown>;
      const id = this.readString(source, 'id') ?? '';
      const eventType = this.readString(source, 'eventType', 'event_type') ?? '';
      const performedAt = this.readString(source, 'performedAt', 'performed_at') ?? '';
      const createdAt = this.readString(source, 'createdAt', 'created_at') ?? '';

      if (!id) {
        return [];
      }

      return [{
        id,
        eventType,
        title: this.readNullableString(source, 'title'),
        notes: this.readNullableString(source, 'notes'),
        performedAt,
        createdAt,
      }];
    });
  }

  private formatLifecycleEvent(event: DeviceLifecycleEventApiItem): string {
    const title = this.cleanString(event.title) ?? this.humanizeToken(event.eventType) ?? 'Event';
    const dateText = this.formatDate(event.performedAt || event.createdAt);

    return dateText === '—' ? title : `${title} (${dateText})`;
  }

  private humanizeToken(value: string | null | undefined): string | null {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return null;
    }

    return normalized
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/^\w/, (char) => char.toUpperCase());
  }

  private formatDate(value: string | null): string {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    const parsed = new Date(normalized);
    if (Number.isNaN(parsed.getTime())) {
      return normalized;
    }

    return parsed.toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }

  private normalizeBaseUrl(value: string): string {
    return value.trim().replace(/\/+$/, '');
  }

  private buildScopedParams(scopedCompanyId: string): HttpParams | undefined {
    if (this.role !== 'system_admin') {
      return undefined;
    }

    if (!scopedCompanyId) {
      return undefined;
    }

    return new HttpParams().set('companyId', scopedCompanyId);
  }

  private cleanString(value: string | null | undefined): string | null {
    const normalized = value?.trim() ?? '';
    return normalized ? normalized : null;
  }

  private readNullableString(source: Record<string, unknown>, ...keys: string[]): string | null {
    const value = this.readString(source, ...keys);
    return value === undefined ? null : value;
  }

  private hasAnyKey(source: Record<string, unknown>, ...keys: string[]): boolean {
    return keys.some((key) => Object.prototype.hasOwnProperty.call(source, key));
  }

  private readString(source: Record<string, unknown>, ...keys: string[]): string | undefined {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string') {
        return value;
      }
    }
    return undefined;
  }

  private normalizeStatus(value: string | undefined): DeviceStatus | null {
    if (value === 'active' || value === 'maintenance' || value === 'broken' || value === 'retired') {
      return value;
    }
    return null;
  }

  private extractPhotoPreviews(
    value: Array<{ key?: string; url?: string } | string>,
  ): Array<{ key?: string; url: string; name?: string }> {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.flatMap((item) => {
      if (typeof item === 'string') {
        const url = item.trim();
        if (!url) {
          return [];
        }
        return [{ url }];
      }

      if (item && typeof item === 'object') {
        const key = typeof item.key === 'string' ? item.key.trim() : undefined;
        const url = typeof item.url === 'string' ? item.url.trim() : '';
        if (!url) {
          return [];
        }
        return [{
          key,
          url,
          name: key ? this.fileNameFromPathLike(key) : this.fileNameFromPathLike(url),
        }];
      }

      return [];
    });
  }

  private fileNameFromPathLike(value: string): string {
    const normalized = value.split('?')[0];
    const raw = normalized.split('/').pop() || normalized;
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }

  private readableId(value: string | null | undefined): string {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    if (this.isUuidLike(normalized)) {
      return `${normalized.slice(0, 8)}...${normalized.slice(-6)}`;
    }

    if (normalized.length > 26) {
      return `${normalized.slice(0, 16)}...${normalized.slice(-6)}`;
    }

    return normalized;
  }

  private isUuidLike(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
  }
}
